import { useEffect, useMemo, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import Layout from './components/Layout';
import QuizPage from './pages/QuizPage';
import RedeemPage from './pages/RedeemPage';
import ProtectedRoute from './components/ProtectedRoute';
import { QuizErrorBoundary } from './components/ErrorBoundary';
import {
  hasSelectedLanguage,
  hasSelectedExamType,
  syncLearningRoutePreferences,
} from './lib/userPreferences';
import ExamSelectionPage from './pages/ExamSelectionPage';
import ExamPublicLandingPage from './pages/ExamPublicLandingPage';
import ExamWorkspacePage from './pages/ExamWorkspacePage';
import ExamTrainingEntryPage from './pages/ExamTrainingEntryPage';
import ExamMockExamPage from './pages/ExamMockExamPage';
import ExamAbilityDiagnosisPage from './pages/ExamAbilityDiagnosisPage';
import LearningReportPage from './pages/LearningReportPage';
import MistakeBookPage from './pages/MistakeBookPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import ProfilePage from './pages/ProfilePage';
import AchievementsPage from './pages/AchievementsPage';
import {
  buildLearningRouteScope,
  buildAttemptHref,
  canEnterLearningRoute,
  parseAttemptSpec,
  attemptWorkspaceHref,
  isPracticeAttempt,
} from '@examflow/learning-space';
import { useOptionalQuizRuntimeLaunch } from './features/quiz/QuizRuntimeLaunchProvider';
import { DocumentHead } from './features/seo';
import { BrandLogo, LoadingSpinner } from '@examflow/ui';
import { usePlatformSettings } from './features/platform/PlatformSettingsContext';
import { MaintenancePage } from './features/platform/MaintenancePage';
import {
  AnnouncementBanner,
  LearnerGateRoute,
} from './features/platform/LearnerGateRoute';

function AppGuard({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const routeScope = buildLearningRouteScope(new URLSearchParams(location.search));
  const { languageId, examType } = routeScope;

  useEffect(() => {
    syncLearningRoutePreferences({ languageId, examType });
  }, [languageId, examType]);

  if (!canEnterLearningRoute(location.search, hasSelectedLanguage(), hasSelectedExamType())) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

function QuizRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isDiagnosis = params.get('mode') === 'diagnosis';

  if (isDiagnosis) {
    return <Navigate to="/diagnosis/ability" replace />;
  }

  return (
    <ProtectedRoute requireEntitlement>
      <AppGuard>
        <QuizErrorBoundary>
          <QuizPage />
        </QuizErrorBoundary>
      </AppGuard>
    </ProtectedRoute>
  );
}

function TrainingSessionRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { launchQuizRuntime } = useOptionalQuizRuntimeLaunch();
  const attempt = parseAttemptSpec(location.search);
  const scope = buildLearningRouteScope(new URLSearchParams(location.search));
  const shellHref = useMemo(
    () =>
      scope.languageId && scope.examType
        ? attemptWorkspaceHref({
            languageId: scope.languageId,
            examType: scope.examType,
          })
        : '/workspace',
    [scope.languageId, scope.examType]
  );

  useEffect(() => {
    if (!attempt || !isPracticeAttempt(attempt)) {
      navigate(shellHref, { replace: true });
      return;
    }
    const href = buildAttemptHref(attempt);
    // Land on Exam Space first so SessionRestore (if any) appears there, not on /quiz.
    navigate(shellHref, { replace: true });
    queueMicrotask(() => {
      void launchQuizRuntime(href);
    });
  }, [attempt, launchQuizRuntime, navigate, shellHref]);

  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <LoadingSpinner />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<ExamSelectionPage />} />
      <Route path="/exams/:examId" element={<ExamPublicLandingPage />} />
      <Route path="/redeem" element={<RedeemPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/callback" element={<AuthCallbackPage />} />
      <Route path="/settings" element={<Navigate to="/profile" replace />} />
      <Route path="/verify-email" element={<Navigate to="/profile" replace />} />
      <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <ProtectedRoute requireEntitlement>
            <AppGuard />
          </ProtectedRoute>
        }
      >
        <Route path="/workspace" element={<ExamWorkspacePage />} />
        <Route path="/training" element={<ExamTrainingEntryPage />} />
        <Route path="/training/session" element={<TrainingSessionRedirect />} />
        <Route
          path="/mock-exam"
          element={
            <LearnerGateRoute gate="mockExam">
              <ExamMockExamPage />
            </LearnerGateRoute>
          }
        />
        <Route path="/diagnosis/ability" element={<ExamAbilityDiagnosisPage />} />
        <Route path="/review" element={<Navigate to="/diagnosis/ability" replace />} />
        <Route
          path="/report"
          element={
            <LearnerGateRoute gate="abilityReport">
              <LearningReportPage />
            </LearnerGateRoute>
          }
        />
        <Route path="/learning-report" element={<Navigate to="/report" replace />} />
        <Route
          path="/mistake-book"
          element={
            <LearnerGateRoute gate="mistakeBook">
              <MistakeBookPage />
            </LearnerGateRoute>
          }
        />
        <Route path="/achievements" element={<AchievementsPage />} />
        <Route path="/bank" element={<Navigate to="/workspace" replace />} />
      </Route>

      <Route element={<Layout />}>
        <Route path="/diagnosis" element={<Navigate to="/diagnosis/ability" replace />} />
        <Route path="quiz" element={<QuizRoute />} />
        <Route path="quiz/:configId" element={<QuizRoute />} />
      </Route>
    </Routes>
  );
}

function App() {
  const { settings, isLoading } = usePlatformSettings();
  const location = useLocation();
  const allowDuringMaintenance =
    location.pathname === '/callback' || location.pathname.startsWith('/login');

  if (isLoading) {
    return (
      <>
        <DocumentHead />
        <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-surface-muted">
          <BrandLogo variant="mark" size={40} />
          <LoadingSpinner />
        </div>
      </>
    );
  }

  if (settings.maintenanceMode && !allowDuringMaintenance) {
    return (
      <>
        <DocumentHead />
        <MaintenancePage />
      </>
    );
  }

  return (
    <>
      <DocumentHead />
      <AnnouncementBanner />
      <AppRoutes />
    </>
  );
}

export default App;
